import { Identifier, useGetOne } from "react-admin";
import { Divider, Chip, Typography } from "@mui/material";
import { formatDateTime } from "../../utils/calendar";
import { GivenAnswer, TheoryExamSimulationResult } from "../../providers/theoryExamSimulationResultsProvider";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { autovioColors } from "../../misc/backofficeTheme";
import { TheoryQuestion } from "../../model/TheoryQuestion";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { CheckedIcon } from "../../icons/CheckedIcon";
import { CircleFalseIcon } from "../../icons/CircleFalseIcon";
import { parseFreeTextAnswer } from "../../utils/parseFreeTextAnswer";

interface TheoryExamSimulationResultViewProps {
  simulationId: Identifier;
  onClick: (question: TheoryQuestion, givenAnswer: GivenAnswer, simulationId: Identifier) => void;
}

export function TheoryExamSimulationResultView({ simulationId, onClick }: TheoryExamSimulationResultViewProps) {
  const {
    data: record,
    error,
    isLoading,
  } = useGetOne<TheoryExamSimulationResult>("theoryExamSimulationResults", {
    id: String(simulationId),
  });

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (!record || error) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Simulation wurde mit einer alten App gemacht, Antworten wurden nicht gespeichert.
      </Typography>
    );
  }

  return (
    <Column spacing={1}>
      <Column>
        <Row spacing={1} alignItems="center">
          <Typography variant="h6">{record.passed ? "Bestanden" : "Nicht bestanden"}</Typography>
          {record.cancelled && (
            <Chip
              label="Abgebrochen"
              size="small"
              sx={{ backgroundColor: "action.disabledBackground", height: "20px" }}
            />
          )}
        </Row>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {`${record.errorPoints} Fehlerpunkte • ${formatDateTime(record.timestamp, true)}`}
        </Typography>
      </Column>
      {record.simulation?.map((section) =>
        section.questions.map((question) => {
          const givenAnswer = record.givenAnswers?.[question.id];
          const isAnswered =
            !!givenAnswer &&
            ((givenAnswer.selectedOptions?.length ?? 0) > 0 ||
              (!!givenAnswer.enteredAnswers?.length &&
                givenAnswer.enteredAnswers.every((answer) => parseFreeTextAnswer(answer) !== undefined)));
          return (
            <_TheoryExamSimulationQuestion
              key={question.id}
              question={question}
              sectionTitle={section.sectionTitle}
              hasBeenAnsweredCorrectly={givenAnswer?.hasBeenAnsweredCorrectly ?? false}
              isAnswered={isAnswered}
              onClick={givenAnswer ? () => onClick(question, givenAnswer, simulationId) : undefined}
            />
          );
        }),
      )}
    </Column>
  );
}

const _TheoryExamSimulationQuestion = ({
  question,
  sectionTitle,
  hasBeenAnsweredCorrectly,
  isAnswered,
  onClick,
}: {
  question: TheoryQuestion;
  sectionTitle: string;
  hasBeenAnsweredCorrectly: boolean;
  isAnswered: boolean;
  onClick?: () => void;
}) => {
  return (
    <>
      <Row
        alignItems="center"
        justifyContent="space-between"
        py={1}
        onClick={onClick}
        sx={onClick ? { cursor: "pointer" } : undefined}
      >
        <Column spacing={0.5}>
          <Typography variant="body2" sx={!isAnswered ? { color: "text.secondary" } : {}}>
            {question.title}
          </Typography>
          <Row spacing={3}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {question.id}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {sectionTitle === "Grundstoff" ? sectionTitle : `Zusatzstoff Klasse ${sectionTitle}`}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {question.points} Punkte
            </Typography>
          </Row>
        </Column>
        <Row spacing={0.25} alignItems="center">
          {!isAnswered ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Nicht beantwortet
            </Typography>
          ) : hasBeenAnsweredCorrectly ? (
            <CheckedIcon sx={{ color: autovioColors.green, fontSize: 20 }} />
          ) : (
            <CircleFalseIcon sx={{ color: autovioColors.red, fontSize: 20 }} />
          )}
          <NavigateNextIcon sx={{ color: "text.secondary", fontSize: 20 }} />
        </Row>
      </Row>
      <Divider />
    </>
  );
};
